import { forwardRef } from 'react';
import { IconName } from '@optimus-web/icons';
import { Button } from '../Button';
import { Grid } from '../Grid';
import { VisuallyHidden } from '../helpers';
import { IconButton } from '../IconButton';
import { Stack, StackItem } from '../Stack';
import { Textarea } from '../Textarea';
import { Typography, TypographyVariant } from '../typography';

interface ChatTextareaProps {
    autoCollapse?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    forwardedRef?: React.Ref<HTMLTextAreaElement>;
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyPress: () => void;
    onSubmit: () => void;
    placeholder?: React.ReactNode;
    value: string;
    name: string;
    iconAccessibleText?: React.ReactChild;
    label?: React.ReactNode;
    footerMessage?: React.ReactNode;
    footerMessageVariant?: TypographyVariant;
    newLayout?: boolean;
}

const ChatTextarea = ({
    disabled,
    footerMessage,
    forwardedRef,
    onSubmit,
    iconAccessibleText,
    label,
    name,
    footerMessageVariant = 'primary',
    newLayout = false,
    ...textareaProps
}: ChatTextareaProps) => (
    <Stack vertical fill spacing={newLayout ? '150' : '0'}>
        <VisuallyHidden>
            <label htmlFor={name}>{label}</label>
        </VisuallyHidden>
        <Stack fill distribution="stretch" spacing="150">
            <StackItem grow>
                <Textarea
                    autosize
                    rows={1}
                    collapsedHeight={20}
                    ref={forwardedRef}
                    maxHeight={90}
                    name={name}
                    {...textareaProps}
                />
            </StackItem>
            {!newLayout && (
                <StackItem contentFit>
                    <IconButton
                        size="medium"
                        disabled={disabled}
                        onClick={onSubmit}
                        icon={IconName.SendMessage}
                        accessibleText={iconAccessibleText}
                        type="submit"
                    />
                </StackItem>
            )}
        </Stack>
        {newLayout && (
            <Grid templateColumns="auto min-content" gap="100" alignItems="end">
                {footerMessage ? (
                    <Typography as="div" textStyle="bodySmall" variant={footerMessageVariant}>
                        {footerMessage}
                    </Typography>
                ) : (
                    <span />
                )}
                <Button variant="primary" size="small" disabled={disabled} onClick={onSubmit} type="submit">
                    {iconAccessibleText}
                </Button>
            </Grid>
        )}
        {!newLayout && footerMessage && (
            <StackItem padding={footerMessage ? '150' : '100'}>
                <Typography as="div" textStyle="bodySmall" align="center" variant={footerMessageVariant}>
                    {footerMessage}
                </Typography>
            </StackItem>
        )}
    </Stack>
);

export default forwardRef<HTMLTextAreaElement, ChatTextareaProps>((props, ref) => (
    <ChatTextarea forwardedRef={ref} {...props} />
));
